import type { Company, Contact } from "@/components/atomic-crm/types";
import {
  type ClientBillingRecipients,
  resolveClientBillingRecipients,
} from "../../../supabase/functions/_shared/clientRecipientPolicy.ts";

const pickPrimaryEmailRow = (
  rows?: Array<{ email?: string | null; isPrimary?: boolean }> | null,
) => {
  const list = rows ?? [];
  const flagged = list
    .find((row) => row.isPrimary && row.email?.trim())
    ?.email?.trim();
  if (flagged) return flagged;
  return list.find((row) => row.email?.trim())?.email?.trim() ?? "";
};

const pickPrimaryPhoneRow = (
  rows?: Array<{ number?: string | null; isPrimary?: boolean }> | null,
) => {
  const list = rows ?? [];
  const flagged = list
    .find((row) => row.isPrimary && row.number?.trim())
    ?.number?.trim();
  if (flagged) return flagged;
  return list.find((row) => row.number?.trim())?.number?.trim() ?? "";
};

export const getContactPrimaryEmail = (contact?: Contact | null) =>
  pickPrimaryEmailRow(contact?.email_jsonb);

export const getContactPrimaryPhone = (contact?: Contact | null) =>
  pickPrimaryPhoneRow(contact?.phone_jsonb);

export const getCompanyPrimaryContactEmail = (company?: Company | null) =>
  pickPrimaryEmailRow(company?.primary_contact_email_jsonb);

export const getCompanyPrimaryContactPhone = (company?: Company | null) =>
  pickPrimaryPhoneRow(company?.primary_contact_phone_jsonb);

/** Current billing recipients for a linked client (shared with edge functions). */
export const resolveBillingRecipients = ({
  company,
  contact,
}: {
  company?: Company | null;
  contact?: Contact | null;
}): ClientBillingRecipients =>
  resolveClientBillingRecipients({ company, contact });

/** Company billing: designated recipients, else primary contact, else company channels. */
export const resolveCompanyBillingEmail = (company?: Company | null): string =>
  company ? (resolveClientBillingRecipients({ company }).emails[0] ?? "") : "";

export const resolveCompanyBillingPhone = (company?: Company | null): string =>
  company ? (resolveClientBillingRecipients({ company }).phones[0] ?? "") : "";

export type BillingRecipientContext = {
  company?: Company | null;
  contact?: Contact | null;
  ticketRequesterEmail?: string | null;
  fallbackEmail?: string | null;
};

/**
 * All current billing emails. Historical addresses are only used for
 * tickets/invoices without a linked client.
 */
export const resolveBillingRecipientEmails = ({
  company,
  contact,
  ticketRequesterEmail,
  fallbackEmail,
}: BillingRecipientContext): string[] => {
  // Linked records are authoritative: do not revive an old ticket/invoice address.
  if (contact || company) {
    return resolveClientBillingRecipients({ company, contact }).emails;
  }
  const historical = ticketRequesterEmail?.trim() || fallbackEmail?.trim();
  return historical ? [historical] : [];
};

/** First billing email, for single-recipient fields and display. */
export const resolveBillingRecipientEmail = (
  context: BillingRecipientContext,
): string => resolveBillingRecipientEmails(context)[0] ?? "";

export type BillingRecipientPhoneContext = {
  company?: Company | null;
  contact?: Contact | null;
};

export const resolveBillingRecipientPhones = ({
  company,
  contact,
}: BillingRecipientPhoneContext): string[] =>
  contact || company
    ? resolveClientBillingRecipients({ company, contact }).phones
    : [];

/** First billing phone, for single-recipient fields and display. */
export const resolveBillingRecipientPhone = (
  context: BillingRecipientPhoneContext,
): string => resolveBillingRecipientPhones(context)[0] ?? "";
